/**
 * Geo helpers — normalise between the backend's dual location fields
 * (`position` + `geometry`) and the old single `location` GeoJSON shape.
 *
 * Backend schema (device & asset):
 *   position  : { latitude: Number, longitude: Number }
 *   geometry  : { type: 'Point', coordinates: [lng, lat] }
 *
 * Legacy frontend shape (pre-migration):
 *   location  : { type: 'Point', coordinates: [lng, lat] }
 */

/**
 * Extract [lng, lat] from any of the three shapes.
 * Returns `null` when no valid coordinates are found.
 *
 * @param {Object} entity - device / asset / shadow record
 * @returns {[number, number] | null}  [longitude, latitude]
 */
export function extractCoords(entity) {
  if (!entity) return null

  // 1. geometry  (GeoJSON Point)
  const g = entity.geometry
  if (g?.coordinates?.length === 2) return g.coordinates

  // 2. position  { latitude, longitude }
  const p = entity.position
  if (p?.latitude != null && p?.longitude != null) return [p.longitude, p.latitude]

  // 3. legacy location  (GeoJSON Point)
  const l = entity.location
  if (l?.coordinates?.length === 2) return l.coordinates

  return null
}

/**
 * Build a position + geometry pair for write payloads.
 *
 * @param {number} lng - Longitude
 * @param {number} lat - Latitude
 * @returns {{ position: Object, geometry: Object }}
 */
export function buildLocationPayload(lng, lat) {
  return {
    position: { latitude: Number(lat), longitude: Number(lng) },
    geometry: {
      type: 'Point',
      coordinates: [Number(lng), Number(lat)],
    },
  }
}

/**
 * Convenience: has valid coordinates?
 *
 * @param {Object} entity
 * @returns {boolean}
 */
export function hasCoords(entity) {
  return extractCoords(entity) !== null
}

/**
 * Extract { latitude, longitude } from any shape.
 *
 * @param {Object} entity
 * @returns {{ latitude: number, longitude: number } | null}
 */
export function extractPosition(entity) {
  const c = extractCoords(entity)
  if (!c) return null
  return { latitude: c[1], longitude: c[0] }
}
